import { useRef } from 'react';
import { Control, Controller, useFormContext } from 'react-hook-form';
import { TextInputProps } from '@mantine/core';
import { AsYouType, CountryCode } from 'libphonenumber-js';

import { TextInput } from '@monorepo/components';

interface IControlledPhoneInput extends TextInputProps {
  name: string;
  control?: Control<any>;
}

const FALLBACK_PHONE_COUNTRY: CountryCode = 'US';

const getDefaultPhoneCountry = (): CountryCode => {
  if (typeof navigator === 'undefined') {
    return FALLBACK_PHONE_COUNTRY;
  }

  const locale = navigator.languages?.[0] || navigator.language || '';
  const localeParts = locale.split('-').filter(Boolean);
  const localeRegion = localeParts
    .slice(1)
    .reverse()
    .find((part) => /^[A-Za-z]{2}$/.test(part))
    ?.toUpperCase();

  if (/^[A-Z]{2}$/.test(localeRegion || '')) {
    return localeRegion as CountryCode;
  }

  return FALLBACK_PHONE_COUNTRY;
};

const ControlledPhoneInput: React.FC<IControlledPhoneInput> = ({ name, control, ...props }) => {
  const form = useFormContext();
  const effectiveControl = form?.control ?? control;
  const defaultCountryRef = useRef<CountryCode>(getDefaultPhoneCountry());

  if (!effectiveControl) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `ControlledPhoneInput "${name}" must be used inside a <FormProvider>. Rendering uncontrolled fallback.`
      );
    }
    return <TextInput {...props} />;
  }

  return (
    <Controller
      control={effectiveControl}
      name={name}
      render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
        <TextInput
          ref={ref}
          value={value || ''}
          onChange={(e) => {
            const inputValue = e.target.value;
            const formatter = inputValue.trim().startsWith('+')
              ? new AsYouType()
              : new AsYouType(defaultCountryRef.current);
            const formattedValue = formatter.input(inputValue);
            onChange(formattedValue);
          }}
          error={error?.message}
          {...props}
        />
      )}
    />
  );
};

export default ControlledPhoneInput;
